import { Head, Link, Button } from "zudoku/components";
import { useState } from "react";
import { XmlFormatter } from "../components/XmlFormatter";

const apiExamples = {
  onapi: {
    title: "On API - Video data and schedules",
    link: "/video/on-api/about-the-on-api",
    code: `curl --request GET \
  --url 'http://on-api.gracenote.com//v3/Celebrities?api_key=<string>'`
  },
  gmd: {
    title: "GMD API - Look up Music Data by Artists, Albums, Recordings",
    link: "/audio/gmd-api-v2/Overview",
    code: `curl --request GET \
  --url 'https://api.gmd.music.gracenote.com/v2/artists/search?artistName=Lil%20Nas%20X' \
  --header 'GN-APIKEY: your-api-key-here'`
  },
  gsd: {
    title: "Global Sports Data - Sports statistics",
    link: "/sports/index",
    code: `curl --request GET \
  --url 'https://api.sports.gracenote.com//gns-api/sports?api_key=<string>'`
  },
  nexus: {
    title: "Nexus Auto SDK - Automotive platform",
    link: "/automotive/index",
    code: `curl --request GET \
  --url 'https://api.nexus.gracenote.com/v1/programBundles?preferredLanguage=<value>&contentMarket=USA' \
  --header 'GN-APIKEY: your-api-key-here'`
  },
  gnids: {
    title: "GN IDs API - ID distribution",
    link: "/video/gn-ids-api/about-gn-ids-api",
    code: `curl --request POST \
  --url https://gnids.gracenote.com/api/v1/movies \
  --header 'Content-Type: application/json' \
  --header 'GN-APIKEY: your-api-key-here' \
  --data '
{
  "title": {
    "language": "language",
    "value": "value"
  },
  "subType": "subType",
  "type": "type"
}
'`
  }
};

export const HomePage = () => {
  const [selectedApi, setSelectedApi] = useState('onapi');

  return (
    <section>
      <XmlFormatter />
      <Head>
        <title>Home - Gracenote Documentation</title>
      </Head>
      
      {/* Hero Section */}
      <div className="grid lg:grid-cols-2 gap-12 items-center py-16">
        <div>
          <h1 className="text-5xl font-extrabold mb-4">Welcome to Gracenote Docs </h1>
          <p className="text-xl text-muted-foreground">
            Browse Gracenote product documentation to learn how to build Gracenote media and entertainment metadata solutions. Here you will find developer guides, API explorers, API and SDK reference content, and links to Gracenote apps and widgets.
          </p>
        </div>
        <div className="flex justify-end">
          <img src="/home-banner-1-1.png" alt="Gracenote Platform" className="max-w-[650px] w-full" />
        </div>
      </div>


      {/* Product Categories */}
      <div className="py-16 border-t">
        <h1 className="text-4xl font-bold mb-2">Learn About Gracenote Products</h1>
        <h2 className="text-xl text-muted-foreground mb-12">Explore each category to understand Gracenote products and solutions.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="border rounded-lg p-6 bg-card hover:border-primary transition-colors">
            <Link to="/video/index" className="block">
              <h3 className="text-xl font-bold mb-2">Video Products</h3>
              <p className="text-muted-foreground text-sm">Gracenote video data and IDs fuels next gen guide, search and discovery experiences, streamlines content distribution and informs project development.</p>
            </Link>
          </div>
          <div className="border rounded-lg p-6 bg-card hover:border-primary transition-colors">
            <Link to="/audio/index" className="block">
              <h3 className="text-xl font-bold mb-2">Audio Products</h3>
              <p className="text-muted-foreground text-sm">Gracenote's music recognition technology and metadata help hundreds of millions of music fans globally to identify, discover, and connect with the songs and artists they love every day.</p>
            </Link>
          </div>
          <div className="border rounded-lg p-6 bg-card hover:border-primary transition-colors">
            <Link to="/sports/index" className="block">
              <h3 className="text-xl font-bold mb-2">Sports Products</h3>
              <p className="text-muted-foreground text-sm">Gracenote provides in-depth sports data, including schedules, scores, play-by-play stats, and team and player information, for all of the major professional leagues around the world.</p>
            </Link>
          </div>
          <div className="border rounded-lg p-6 bg-card hover:border-primary transition-colors">
            <Link to="/automotive/index" className="block">
              <h3 className="text-xl font-bold mb-2">Automotive Products</h3>
              <p className="text-muted-foreground text-sm">Reimagine in-car entertainment with an automotive data platform that enables automakers to provide radio, streaming music, podcasts, sports, movies and TV in the car. </p>
            </Link>
          </div>
        </div>
      </div>

      {/* Video MCP Server */}
      <div className="py-16 border-t">
        <div className="grid lg:grid-cols-2 gap-8 items-center">
          <div className="border rounded-lg p-8 bg-card hover:border-primary transition-colors">
            <h1 className="text-4xl font-bold mb-2">Video MCP Server</h1>
            <h2 className="text-xl text-muted-foreground mb-4">Unlock AI-powered content search and discovery.</h2>
            <p className="text-muted-foreground mb-6">
              The Gracenote Video Model Context Protocol (MCP) Server grounds LLM responses with Gracenote's gold-standard video data, verifying and enriching the output. With LLMs and Gracenote Video MCP Server, entertainment providers can turbocharge AI-driven content search and recommendations to deepen audience engagement.
            </p>
            <Button asChild>
              <Link to="/video/video-mcp-server/intro">Learn More →</Link>
            </Button>
          </div>
          <div className="flex justify-center">
            <img src="/images/video-mcp-server-use-cases.webp" alt="Video MCP Server Use Cases" className="rounded-lg w-full max-w-[500px] max-h-[300px] object-cover" />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="py-16 border-t">
        <h1 className="text-4xl font-bold mb-2">Get Started</h1>
        <h2 className="text-xl text-muted-foreground mb-12">Register for API access or contact our support team for assistance.</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          <div className="border rounded-lg p-8 bg-card hover:border-primary transition-colors">
            <h3 className="text-2xl font-bold mb-4">Register for an Account</h3>
            <p className="text-muted-foreground mb-6">
              Register a new API Management ID to access developer portal and get your API keys to start building.
            </p>
            <Button asChild>
              <Link to="/register">Register →</Link>
            </Button>
          </div>
          <div className="border rounded-lg p-8 bg-card hover:border-primary transition-colors">
            <h3 className="text-2xl font-bold mb-4">Contact Support</h3>
            <p className="text-muted-foreground mb-6">
              Need help with integration or have questions? Our support team is here to assist you with technical guidance and best practices.
            </p>
            <Button asChild variant="outline" className="hover:bg-primary hover:text-primary-foreground">
              <a href="https://nielsen113.zendesk.com/auth/v2/login/signin?return_to=https%3A%2F%2Fsupport.gracenote.com%2Fhc%2Fen-us&theme=hc&locale=en-us&brand_id=19094754412817&auth_origin=19094754412817%2Ctrue%2Ctrue" target="_blank" rel="noopener noreferrer">Contact us →</a>
            </Button>
          </div>
        </div>
      </div>

      {/* Build with Gracenote - Code Example */}
      <div className="py-16 border-t">
        <h2 className="text-3xl font-bold mb-8">Explore Gracenote APIs</h2>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-3 space-y-4">
            <button 
              onClick={() => setSelectedApi('onapi')} 
              className={`w-full text-left px-4 py-2 border rounded-md transition-colors ${
                selectedApi === 'onapi' 
                  ? 'bg-primary text-primary-foreground border-primary' 
                  : 'bg-card hover:bg-primary hover:text-primary-foreground'
              }`}
            >
              On API - Video data and schedules
            </button>
            <button 
              onClick={() => setSelectedApi('gmd')} 
              className={`w-full text-left px-4 py-2 border rounded-md transition-colors ${
                selectedApi === 'gmd' 
                  ? 'bg-primary text-primary-foreground border-primary' 
                  : 'bg-card hover:bg-primary hover:text-primary-foreground'
              }`}
            >
              GMD API - Look up Music Data by Artists, Albums, Recordings.
            </button>
            <button 
              onClick={() => setSelectedApi('gsd')} 
              className={`w-full text-left px-4 py-2 border rounded-md transition-colors ${
                selectedApi === 'gsd' 
                  ? 'bg-primary text-primary-foreground border-primary' 
                  : 'bg-card hover:bg-primary hover:text-primary-foreground'
              }`}
            >
              Global Sports Data - Sports statistics
            </button>
            <button 
              onClick={() => setSelectedApi('nexus')} 
              className={`w-full text-left px-4 py-2 border rounded-md transition-colors ${
                selectedApi === 'nexus' 
                  ? 'bg-primary text-primary-foreground border-primary' 
                  : 'bg-card hover:bg-primary hover:text-primary-foreground'
              }`}
            >
              Nexus Auto SDK - Automotive platform
            </button>
            <button 
              onClick={() => setSelectedApi('gnids')} 
              className={`w-full text-left px-4 py-2 border rounded-md transition-colors ${
                selectedApi === 'gnids' 
                  ? 'bg-primary text-primary-foreground border-primary' 
                  : 'bg-card hover:bg-primary hover:text-primary-foreground'
              }`}
            >
              GN IDs API - ID distribution
            </button>
          </div>
          <div className="lg:col-span-9 border rounded-lg bg-card">
            <div className="border-b px-4 py-2 bg-muted/50">
              <span className="text-sm font-medium">cURL</span>
            </div>
            <pre className="p-6 overflow-x-auto text-sm bg-black">
              <code style={{color: '#C3E88D'}}>{apiExamples[selectedApi].code}</code>
            </pre>
            <div className="border-t p-4 flex justify-end">
              <Button size="sm" asChild>
                <Link to={apiExamples[selectedApi].link}>Try in API Explorer →</Link>
              </Button>
            </div>
          </div>
        </div>

      </div>


      {/* Popular Resources */}
      <div className="py-16 border-t">
        <h2 className="text-3xl font-bold mb-4">Documentation & Guides</h2>
        <p className="text-muted-foreground mb-12">Learn how to integrate and implement Gracenote solutions</p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/video/video-mcp-server/intro" className="block">
              <h3 className="font-semibold mb-1">Video MCP Server</h3>
              <p className="text-sm text-muted-foreground">AI-powered video metadata for LLMs</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/video/on-api/about-the-on-api" className="block">
              <h3 className="font-semibold mb-1">On API</h3>
              <p className="text-sm text-muted-foreground">Entertainment metadata and program schedules</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/video/GVD/overview" className="block">
              <h3 className="font-semibold mb-1">GVD</h3>
              <p className="text-sm text-muted-foreground">Comprehensive video metadata database</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/video/on-connect-lookup-apis/index" className="block">
              <h3 className="font-semibold mb-1">On-Connect Lookup API</h3>
              <p className="text-sm text-muted-foreground">Real-time TV guide and program lookup</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/video/gn-ids-api/about-gn-ids-api" className="block">
              <h3 className="font-semibold mb-1">GN IDs API</h3>
              <p className="text-sm text-muted-foreground">Content identification and registration</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/video/Gracenote-View/gracenote-view" className="block">
              <h3 className="font-semibold mb-1">GN View</h3>
              <p className="text-sm text-muted-foreground">Web-based video metadata browser</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/audio/gmd-api-v2/Overview" className="block">
              <h3 className="font-semibold mb-1">GMD API v2</h3>
              <p className="text-sm text-muted-foreground">Music identification and metadata</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/sports/Global-Sports-Data/global-sports-data-overview" className="block">
              <h3 className="font-semibold mb-1">Global Sports Data</h3>
              <p className="text-sm text-muted-foreground">Comprehensive sports statistics API</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/sports/Global-Sports-Data/global-sports-connected-overview" className="block">
              <h3 className="font-semibold mb-1">Global Sports Connected</h3>
              <p className="text-sm text-muted-foreground">Sports content for TV platforms</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/automotive/nexus-auto/nexus-api-protected" className="block">
              <h3 className="font-semibold mb-1">Nexus Auto API</h3>
              <p className="text-sm text-muted-foreground">In-vehicle entertainment API</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/automotive/nexus-auto/nexus-sdk-protected" className="block">
              <h3 className="font-semibold mb-1">Nexus Auto SDK</h3>
              <p className="text-sm text-muted-foreground">Automotive integration SDK</p>
            </Link>
          </div>
          <div className="border rounded-lg p-4 bg-card hover:border-primary transition-colors">
            <Link to="/automotive/rsid-api/rsid-api-dev-guide" className="block">
              <h3 className="font-semibold mb-1">RSID</h3>
              <p className="text-sm text-muted-foreground">Radio station identification service</p>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
